
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Invoice, Client, statusColors, statusLabels } from "@/types/invoice";
import { useTranslation } from "@/i18n/useTranslation";
import { Language } from "@/i18n/translations";
import { Link } from "react-router-dom";

interface InvoiceTableProps {
  invoices: Invoice[];
  clients: Client[];
  language: Language;
}

const InvoiceTable = ({ invoices, clients, language }: InvoiceTableProps) => {
  const { t } = useTranslation();

  const formatCurrency = (amount: number, currencyCode: string) => {
    const currencySymbols: Record<string, string> = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CNY': '¥',
      'INR': '₹',
      'BRL': 'R$',
      'MXN': '$',
      'CAD': 'C$',
      'AUD': 'A$'
    };
    
    const symbol = currencySymbols[currencyCode] || currencyCode;
    return `${symbol}${amount.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}`;
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('invoices.invoiceNumber')}</TableHead>
            <TableHead>{t('invoices.client')}</TableHead>
            <TableHead>{t('invoices.amount')}</TableHead>
            <TableHead>{t('invoices.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.length > 0 ? (
            invoices.map((invoice) => {
              const client = clients.find(c => c.id === invoice.client_id);
              return (
                <TableRow key={invoice.id}>
                  <TableCell className="font-medium">
                    <Link 
                      to={`/invoice/${invoice.id}`} 
                      className="text-primary hover:underline"
                    >
                      {invoice.invoice_number}
                    </Link>
                  </TableCell>
                  <TableCell>{client ? client.name : `Client #${invoice.client_id}`}</TableCell>
                  <TableCell>
                    {formatCurrency(invoice.amount, invoice.currency)}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant="secondary"
                      className={statusColors[invoice.status]}
                    >
                      {statusLabels[invoice.status]}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                {t('invoices.noInvoicesInPeriod')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default InvoiceTable;
